/**
 * PROTOCOL ΔΣ — Propaganda Engine admin panel.
 *
 * Surface for the operator to:
 *   • flip the global Panic Kill Switch,
 *   • enable / disable triggers and tweak their policy + cooldown,
 *   • CRUD the message templates per trigger,
 *   • review the approval queue and approve / reject pending messages,
 *   • inspect the audit feed.
 *
 * Auth: every API call rides the admin JWT stored in sessionStorage. The
 * panic switch and approve / reject actions require 2FA on the backend —
 * the UI surfaces the resulting 403 with a clear toast pointing the user
 * to /admin/security.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Brain,
  CheckCircle2,
  Clock,
  Crown,
  Flame,
  ListChecks,
  Loader2,
  MessageSquareText,
  Pause,
  Pencil,
  Plus,
  Power,
  RadioTower,
  Settings as SettingsIcon,
  ShieldOff,
  Trash2,
  X,
  Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

import { getAdminToken } from "@/lib/adminAuth";
import { logger } from "@/lib/logger";
import WhaleWatcherTab from "@/pages/admin/sections/WhaleWatcherTab";
import CabinetTab from "@/pages/propaganda/CabinetTab";

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------
const API: string = process.env.REACT_APP_BACKEND_URL || "";

interface Trigger {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  policy: "auto" | "approval";
  cooldown_minutes: number;
  last_fired_at: string | null;
  fire_count: number;
  metadata: Record<string, unknown>;
}

interface Template {
  id: string;
  trigger_key: string;
  language: "en" | "fr";
  content: string;
  weight: number;
  mentions_vault: boolean;
  enabled: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

interface QueueItem {
  id: string;
  trigger_key: string;
  template_id: string | null;
  rendered_content: string;
  platforms: string[];
  payload: Record<string, unknown>;
  status:
    | "proposed"
    | "approved"
    | "sent"
    | "failed"
    | "rejected"
    | "killed";
  manual: boolean;
  proposed_at: string;
  approved_at: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  error: string | null;
  reject_reason: string | null;
  // Sprint 17.5d — per-platform dispatch outcomes, populated by the
  // dispatch_worker. Used to surface "View on X" / "View on Telegram"
  // links once a tweet/message ID is known.
  results?: Record<string, {
    outcome: string;
    platform_message_id?: string | null;
    error?: string | null;
    response_snippet?: string | null;
    dry_run?: boolean;
  }>;
}

interface ActivityEvent {
  id: string;
  type: string;
  trigger_key: string | null;
  queue_item_id: string | null;
  by_jti: string | null;
  ip: string | null;
  at: string;
  meta: Record<string, unknown>;
}

interface Settings {
  panic: boolean;
  default_locale: string;
  vault_link_every: number;
  vault_mention_counter: number;
  rate_limits: { per_hour: number; per_day: number; per_trigger_minutes: number };
  default_delay_seconds_min: number;
  default_delay_seconds_max: number;
  platforms: string[];
  // Sprint 13.2 — Tone Engine
  llm_enabled: boolean;
  llm_enhance_ratio: number;
  personality_prompt: string;
  llm_provider: string;
  llm_model: string;
  // Sprint 13.3 — Dispatch worker
  dispatch_enabled?: boolean;
  dispatch_dry_run?: boolean;
}

// ---------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------
function authHeaders() {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function handleError(err: unknown, fallback: string) {
  // eslint-disable-next-line
  const detail = (err as any)?.response?.data?.detail;
  // eslint-disable-next-line
  const status = (err as any)?.response?.status;
  if (status === 403 && detail?.code === "TWOFA_REQUIRED") {
    toast.error("2FA required for this action", {
      description: "Enable 2FA from Admin → Security first.",
    });
    return;
  }
  toast.error(typeof detail === "string" ? detail : fallback);
  logger.error(err);
}

// ---------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------
export default function Propaganda() {
  const navigate = useNavigate();
  const token = getAdminToken();

  const [settings, setSettings] = useState<Settings | null>(null);
  const [tab, setTab] = useState<string>("triggers");
  const [busyPanic, setBusyPanic] = useState<boolean>(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await axios.get<Settings>(
        `${API}/api/admin/propaganda/settings`,
        { headers: authHeaders() },
      );
      setSettings(data);
    } catch (err) {
      handleError(err, "Failed to load settings");
    }
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    void loadSettings();
  }, [token, navigate, loadSettings]);

  const togglePanic = async (next: boolean) => {
    setBusyPanic(true);
    try {
      const { data } = await axios.post<Settings>(
        `${API}/api/admin/propaganda/panic`,
        { panic: next },
        { headers: authHeaders() },
      );
      setSettings(data);
      toast.success(
        next ? "Panic ON — pending messages killed." : "Panic OFF — engine resumed.",
      );
    } catch (err) {
      handleError(err, "Failed to toggle panic");
    } finally {
      setBusyPanic(false);
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground p-6 md:p-10"
      data-testid="propaganda-page"
    >
      <header className="max-w-6xl mx-auto mb-8">
        <button
          type="button"
          onClick={() => navigate("/admin")}
          className="text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4"
          data-testid="propaganda-back"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-muted-foreground uppercase">
              $DEEPOTUS / PROTOCOL ΔΣ
            </div>
            <h1 className="text-2xl md:text-3xl font-medium mt-1">
              Propaganda Engine
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Scenario-based messaging fleet. Trigger detectors fire into an
              approval queue; the dispatcher posts to X + Telegram with a
              humanized 10–30 s delay.
            </p>
          </div>

          <PanicCard
            panic={settings.panic}
            busy={busyPanic}
            onToggle={togglePanic}
          />
        </div>
      </header>

      <DispatchModeBanner
        enabled={settings.dispatch_enabled ?? false}
        dryRun={settings.dispatch_dry_run ?? true}
        panic={settings.panic}
      />

      <main className="max-w-6xl mx-auto">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-7 w-full md:w-auto">
            <TabsTrigger value="triggers" data-testid="propaganda-tab-triggers">
              <Flame size={14} className="mr-2" /> Triggers
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="propaganda-tab-templates">
              <MessageSquareText size={14} className="mr-2" /> Templates
            </TabsTrigger>
            <TabsTrigger value="queue" data-testid="propaganda-tab-queue">
              <ListChecks size={14} className="mr-2" /> Queue
            </TabsTrigger>
            <TabsTrigger value="tone" data-testid="propaganda-tab-tone">
              <Brain size={14} className="mr-2" /> Tone & LLM
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="propaganda-tab-activity">
              <Activity size={14} className="mr-2" /> Activity
            </TabsTrigger>
            <TabsTrigger value="whales" data-testid="propaganda-tab-whales">
              <RadioTower size={14} className="mr-2" /> Whales
            </TabsTrigger>
            <TabsTrigger value="cabinet" data-testid="propaganda-tab-cabinet">
              <Crown size={14} className="mr-2" /> Cabinet
            </TabsTrigger>
          </TabsList>

          <TabsContent value="triggers" className="mt-6">
            <TriggersTab />
          </TabsContent>
          <TabsContent value="templates" className="mt-6">
            <TemplatesTab />
          </TabsContent>
          <TabsContent value="queue" className="mt-6">
            <QueueTab />
          </TabsContent>
          <TabsContent value="tone" className="mt-6">
            <ToneTab settings={settings} onSaved={loadSettings} />
          </TabsContent>
          <TabsContent value="activity" className="mt-6">
            <ActivityTab />
          </TabsContent>
          <TabsContent value="whales" className="mt-6">
            <WhaleWatcherTab />
          </TabsContent>
          <TabsContent value="cabinet" className="mt-6">
            <CabinetTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------
// Dispatch mode banner (Sprint 13.3.x)
// ---------------------------------------------------------------------
interface DispatchModeBannerProps {
  enabled: boolean;
  dryRun: boolean;
  panic: boolean;
}

/**
 * Read-only at-a-glance status strip telling the operator whether the
 * dispatch worker is actually moving items to X / Telegram.
 *
 * Three meaningful states:
 *   • PAUSED  → enabled=false             (worker reads queue, does nothing)
 *   • DRYRUN  → enabled=true, dryRun=true (full pipeline, no HTTP calls)
 *   • LIVE    → enabled=true, dryRun=false (real posts go out)
 *
 * Panic supersedes all three when ON. We surface that here too rather
 * than rely on the user noticing the PanicCard above.
 */
function DispatchModeBanner({ enabled, dryRun, panic }: DispatchModeBannerProps) {
  let state: "panic" | "paused" | "dryrun" | "live";
  if (panic) state = "panic";
  else if (!enabled) state = "paused";
  else if (dryRun) state = "dryrun";
  else state = "live";

  const config: Record<
    typeof state,
    { label: string; subtitle: string; icon: JSX.Element; cls: string }
  > = {
    panic: {
      label: "PANIC — DISPATCH FROZEN",
      subtitle: "Kill-switch is ON. No outbound message will be sent.",
      icon: <ShieldOff size={14} />,
      cls: "border-[#FF4D4D]/60 bg-[#FF4D4D]/10 text-[#FF4D4D]",
    },
    paused: {
      label: "PAUSED — Worker idle",
      subtitle:
        "Dispatch worker is disabled. Approved items wait in queue until you flip 'enabled' ON.",
      icon: <Pause size={14} />,
      cls: "border-zinc-700 bg-zinc-900/40 text-zinc-300",
    },
    dryrun: {
      label: "DRY-RUN — Pipeline test",
      subtitle:
        "Full pipeline runs, but dispatchers short-circuit the HTTP call. Items move to 'sent' so you can validate before going live.",
      icon: <RadioTower size={14} />,
      cls: "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]",
    },
    live: {
      label: "LIVE — Posts go out for real",
      subtitle:
        "Dispatchers will hit X & Telegram with real HTTP calls. Approved items become live posts.",
      icon: <Zap size={14} />,
      cls: "border-[#18C964]/40 bg-[#18C964]/10 text-[#18C964]",
    },
  };
  const c = config[state];

  return (
    <div
      className={`max-w-6xl mx-auto mb-6 rounded-md border px-4 py-3 ${c.cls}`}
      data-testid="propaganda-dispatch-banner"
      data-dispatch-state={state}
      role="status"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">{c.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.3em] uppercase font-mono">
            {c.label}
          </div>
          <p className="text-[11px] text-foreground/70 mt-1 leading-relaxed">
            {c.subtitle}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Panic kill switch card
// ---------------------------------------------------------------------
interface PanicCardProps {
  panic: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
}

function PanicCard({ panic, busy, onToggle }: PanicCardProps) {
  return (
    <div
      className={`rounded-md border p-4 min-w-[280px] ${
        panic
          ? "border-[#FF4D4D] bg-[#FF4D4D]/10"
          : "border-border bg-background/50"
      }`}
      data-testid="propaganda-panic-card"
    >
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        <ShieldOff size={12} /> Panic kill-switch
      </div>
      <div className="mt-2 flex items-center justify-between gap-4">
        <div>
          <div
            className={`text-sm font-mono ${
              panic ? "text-[#FF4D4D]" : "text-foreground/80"
            }`}
            data-testid="propaganda-panic-status"
          >
            {panic ? "ENGINE FROZEN" : "ENGINE LIVE"}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 max-w-[220px]">
            {panic
              ? "All pending dispatches were killed. New triggers are ignored."
              : "Triggers reach the queue. Auto-policy items will be sent."}
          </p>
        </div>
        <Button
          variant={panic ? "outline" : "destructive"}
          onClick={() => onToggle(!panic)}
          disabled={busy}
          className="rounded-[var(--btn-radius)] min-w-[110px]"
          data-testid="propaganda-panic-toggle"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : (
            <>
              <Power size={14} className="mr-1.5" /> {panic ? "Resume" : "Panic"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Triggers tab
// ---------------------------------------------------------------------
function TriggersTab() {
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fireOpen, setFireOpen] = useState<Trigger | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<{ items: Trigger[] }>(
        `${API}/api/admin/propaganda/triggers`,
        { headers: authHeaders() },
      );
      setTriggers(data.items);
    } catch (err) {
      handleError(err, "Failed to load triggers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = async (key: string, body: Partial<Trigger>) => {
    try {
      await axios.patch(
        `${API}/api/admin/propaganda/triggers/${key}`,
        body,
        { headers: authHeaders() },
      );
      await load();
    } catch (err) {
      handleError(err, "Failed to update trigger");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {triggers.map((t) => (
        <div
          key={t.key}
          className="rounded-md border border-border bg-background/40 p-4"
          data-testid={`trigger-row-${t.key}`}
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-medium">{t.label}</h3>
                <Badge variant={t.enabled ? "default" : "secondary"}>
                  {t.enabled ? "ENABLED" : "DISABLED"}
                </Badge>
                <Badge variant="outline">policy: {t.policy}</Badge>
                <Badge variant="outline">
                  cooldown: {t.cooldown_minutes}m
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
              <div className="text-[11px] text-muted-foreground mt-2 font-mono">
                fired {t.fire_count}× ·{" "}
                {t.last_fired_at
                  ? `last ${new Date(t.last_fired_at).toLocaleString()}`
                  : "never fired"}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-xs">
                <Switch
                  checked={t.enabled}
                  onCheckedChange={(v: boolean) => patch(t.key, { enabled: v })}
                  data-testid={`trigger-toggle-${t.key}`}
                />
                <span className="text-muted-foreground">on/off</span>
              </div>
              <Select
                value={t.policy}
                onValueChange={(v: string) =>
                  patch(t.key, { policy: v as "auto" | "approval" })
                }
              >
                <SelectTrigger
                  className="h-8 w-[130px] text-xs"
                  data-testid={`trigger-policy-${t.key}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">auto-send</SelectItem>
                  <SelectItem value="approval">approval queue</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="default"
                size="sm"
                onClick={() => setFireOpen(t)}
                className="rounded-[var(--btn-radius)]"
                data-testid={`trigger-fire-${t.key}`}
              >
                <Flame size={13} className="mr-1" /> Fire now
              </Button>
            </div>
          </div>
        </div>
      ))}

      {fireOpen && (
        <FireDialog
          trigger={fireOpen}
          onClose={() => setFireOpen(null)}
          onFired={() => {
            setFireOpen(null);
            void load();
            toast.success("Trigger fired — check Queue tab.");
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Manual fire dialog
// ---------------------------------------------------------------------
interface FireDialogProps {
  trigger: Trigger;
  onClose: () => void;
  onFired: () => void;
}

function FireDialog({ trigger, onClose, onFired }: FireDialogProps) {
  const [busy, setBusy] = useState<boolean>(false);
  const [buyLink, setBuyLink] = useState<string>("");
  const [mcTier, setMcTier] = useState<number>(25_000);
  const [whaleAmount, setWhaleAmount] = useState<number>(7);
  const [pumpswapLink, setPumpSwapLink] = useState<string>("");
  // Founder Buy Disclosure (Sprint 16.2)
  const [founderAmount, setFounderAmount] = useState<number>(5);
  const [founderWallet, setFounderWallet] = useState<string>("");
  const [founderMcUsd, setFounderMcUsd] = useState<number>(120_000);
  const [founderTxSig, setFounderTxSig] = useState<string>("");
  const [founderNote, setFounderNote] = useState<string>("");
  // KOL Mention (Sprint 16.4)
  const [kolHandle, setKolHandle] = useState<string>("");
  const [kolTweetText, setKolTweetText] = useState<string>("");
  const [kolTweetUrl, setKolTweetUrl] = useState<string>("");

  const submit = async () => {
    setBusy(true);
    try {
      const payload_override: Record<string, unknown> = {};
      if (trigger.key === "mint" && buyLink) payload_override.buy_link = buyLink;
      if (trigger.key === "mc_milestone") {
        payload_override.mc_tier = mcTier;
        if (buyLink) payload_override.buy_link = buyLink;
      }
      if (trigger.key === "whale_buy") payload_override.whale_amount = whaleAmount;
      if (trigger.key === "pumpswap_migration" && pumpswapLink) {
        payload_override.pumpswap_link = pumpswapLink;
      }
      if (trigger.key === "founder_buy") {
        payload_override.founder_amount_sol = founderAmount;
        payload_override.founder_wallet = founderWallet.trim();
        payload_override.founder_mc_usd = founderMcUsd;
        if (founderTxSig.trim()) payload_override.tx_signature = founderTxSig.trim();
        if (founderNote.trim()) payload_override.founder_note = founderNote.trim();
      }
      if (trigger.key === "kol_mention") {
        payload_override.kol_handle = kolHandle.trim().replace(/^@/, "");
        payload_override.kol_tweet_excerpt = kolTweetText.trim();
        if (kolTweetUrl.trim()) payload_override.kol_tweet_url = kolTweetUrl.trim();
      }
      await axios.post(
        `${API}/api/admin/propaganda/fire`,
        { trigger_key: trigger.key, payload_override },
        { headers: authHeaders() },
      );
      onFired();
    } catch (err) {
      handleError(err, "Failed to fire trigger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent
        className="max-w-md"
        data-testid={`trigger-fire-dialog-${trigger.key}`}
      >
        <DialogHeader>
          <DialogTitle>Manual fire — {trigger.label}</DialogTitle>
          <DialogDescription>
            The detector runs in <strong>manual mode</strong> and pushes the
            chosen template into the queue (
            {trigger.policy === "auto"
              ? "auto-send: scheduled directly"
              : "approval queue: waits for 2FA-locked approve"}
            ).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {(trigger.key === "mint" || trigger.key === "mc_milestone") && (
            <div>
              <Label className="text-xs uppercase tracking-widest">
                Buy link (optional)
              </Label>
              <Input
                value={buyLink}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setBuyLink(e.target.value)}
                placeholder="https://pump.fun/<mint>"
                className="font-mono text-xs"
                data-testid="trigger-fire-buy-link"
              />
            </div>
          )}
          {trigger.key === "mc_milestone" && (
            <div>
              <Label className="text-xs uppercase tracking-widest">
                Milestone tier (USD)
              </Label>
              <Input
                type="number"
                value={mcTier}
                min={1000}
                step={5000}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setMcTier(Number(e.target.value || 0))}
                className="font-mono"
                data-testid="trigger-fire-mc-tier"
              />
            </div>
          )}
          {trigger.key === "whale_buy" && (
            <div>
              <Label className="text-xs uppercase tracking-widest">
                Whale buy amount (SOL)
              </Label>
              <Input
                type="number"
                value={whaleAmount}
                min={1}
                step={0.5}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setWhaleAmount(Number(e.target.value || 0))}
                className="font-mono"
                data-testid="trigger-fire-whale-amount"
              />
            </div>
          )}
          {trigger.key === "pumpswap_migration" && (
            <div>
              <Label className="text-xs uppercase tracking-widest">
                PumpSwap link
              </Label>
              <Input
                value={pumpswapLink}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setPumpSwapLink(e.target.value)}
                placeholder="https://swap.pump.fun/?inputCurrency=..."
                className="font-mono text-xs"
                data-testid="trigger-fire-pumpswap-link"
              />
            </div>
          )}
          {trigger.key === "founder_buy" && (
            <div className="space-y-3">
              <div className="rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-2 text-[10px] text-[#F59E0B] font-mono">
                Founder Buy Disclosure — per Tokenomics §6, every personal
                buy must be pre-announced. The post lands in the approval
                queue and still requires 2FA-locked dispatch.
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest">
                    SOL amount
                  </Label>
                  <Input
                    type="number"
                    min={0.01}
                    step={0.5}
                    value={founderAmount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFounderAmount(Number(e.target.value || 0))}
                    className="font-mono"
                    data-testid="trigger-fire-founder-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">
                    MC at fill (USD)
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step={1000}
                    value={founderMcUsd}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFounderMcUsd(Number(e.target.value || 0))}
                    className="font-mono"
                    data-testid="trigger-fire-founder-mc"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">
                  Founder wallet (Solana pubkey)
                </Label>
                <Input
                  value={founderWallet}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFounderWallet(e.target.value)}
                  placeholder="7gXkHxJzwy5o..."
                  spellCheck={false}
                  className="font-mono text-xs"
                  data-testid="trigger-fire-founder-wallet"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">
                  Tx signature (optional)
                </Label>
                <Input
                  value={founderTxSig}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFounderTxSig(e.target.value)}
                  placeholder="2k7Lp...ZfX9"
                  spellCheck={false}
                  className="font-mono text-xs"
                  data-testid="trigger-fire-founder-tx"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">
                  Note (optional, max 80c)
                </Label>
                <Input
                  value={founderNote}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFounderNote(e.target.value.slice(0, 80))}
                  placeholder="anniversary buy"
                  className="font-mono text-xs"
                  data-testid="trigger-fire-founder-note"
                />
              </div>
            </div>
          )}
          {trigger.key === "kol_mention" && (
            <div className="space-y-3">
              <div className="rounded-md border border-[#22D3EE]/40 bg-[#22D3EE]/5 p-2 text-[10px] text-[#22D3EE] font-mono">
                KOL Mention — simulate or replay a tweet from a watched
                handle. Drains through propaganda approval queue (2FA on
                final dispatch).
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">
                  KOL handle (no @)
                </Label>
                <Input
                  value={kolHandle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setKolHandle(e.target.value)}
                  placeholder="Ansem"
                  className="font-mono text-xs"
                  data-testid="trigger-fire-kol-handle"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">
                  Tweet text (excerpt, max 200c)
                </Label>
                <Input
                  value={kolTweetText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setKolTweetText(e.target.value.slice(0, 200))}
                  placeholder='"Looks like $DEEPOTUS is ..."'
                  className="font-mono text-xs"
                  data-testid="trigger-fire-kol-text"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">
                  Tweet URL (optional)
                </Label>
                <Input
                  value={kolTweetUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setKolTweetUrl(e.target.value)}
                  placeholder="https://x.com/Ansem/status/..."
                  className="font-mono text-xs"
                  data-testid="trigger-fire-kol-url"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="rounded-[var(--btn-radius)]"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="rounded-[var(--btn-radius)]"
            data-testid="trigger-fire-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : (
              <>
                <Flame size={14} className="mr-1" /> Fire
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Templates tab
// ---------------------------------------------------------------------
function TemplatesTab() {
  const [items, setItems] = useState<Template[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterTrigger, setFilterTrigger] = useState<string>("all");
  const [filterLang, setFilterLang] = useState<string>("all");
  const [editing, setEditing] = useState<Template | "new" | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterTrigger !== "all") params.trigger_key = filterTrigger;
      if (filterLang !== "all") params.language = filterLang;
      const { data } = await axios.get<{ items: Template[] }>(
        `${API}/api/admin/propaganda/templates`,
        { headers: authHeaders(), params },
      );
      // Alias `data.items` — same reason as Infiltration.tsx (ESLint
      // exhaustive-deps false positive on `setItems(data.items)`).
      const nextTemplates = data?.items ?? [];
      setItems(nextTemplates);
    } catch (err) {
      handleError(err, "Failed to load templates");
    } finally {
      setLoading(false);
    }
    // exhaustive-deps false-positive: type `{ items: Template[] }`
    // confuses the rule with local `items` state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTrigger, filterLang]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: string) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(
        `${API}/api/admin/propaganda/templates/${id}`,
        { headers: authHeaders() },
      );
      toast.success("Template deleted");
      await load();
    } catch (err) {
      handleError(err, "Failed to delete template");
    }
  };

  const toggle = async (t: Template) => {
    try {
      await axios.patch(
        `${API}/api/admin/propaganda/templates/${t.id}`,
        { enabled: !t.enabled },
        { headers: authHeaders() },
      );
      await load();
    } catch (err) {
      handleError(err, "Failed to toggle template");
    }
  };

  const triggerKeys = useMemo(
    () => ["mint", "mc_milestone", "jeet_dip", "whale_buy", "pumpswap_migration"],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Select value={filterTrigger} onValueChange={setFilterTrigger}>
            <SelectTrigger className="h-9 w-[180px] text-xs">
              <SelectValue placeholder="Filter by trigger" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All triggers</SelectItem>
              {triggerKeys.map((k) => (
                <SelectItem key={k} value={k}>
                  {k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterLang} onValueChange={setFilterLang}>
            <SelectTrigger className="h-9 w-[140px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All languages</SelectItem>
              <SelectItem value="en">EN</SelectItem>
              <SelectItem value="fr">FR</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => setEditing("new")}
          className="rounded-[var(--btn-radius)]"
          data-testid="template-new"
        >
          <Plus size={14} className="mr-1" /> New template
        </Button>
      </div>

      {(() => {
        if (loading) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin" size={20} />
            </div>
          );
        }
        if (items.length === 0) {
          return (
            <div className="text-center text-sm text-muted-foreground py-12">
              No templates match these filters.
            </div>
          );
        }
        return (
          <div className="space-y-2">
            {items.map((t) => (
            <div
              key={t.id}
              className="rounded-md border border-border bg-background/40 p-3"
              data-testid={`template-row-${t.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className="font-mono">
                      {t.trigger_key}
                    </Badge>
                    <Badge variant="outline" className="font-mono">
                      {t.language.toUpperCase()}
                    </Badge>
                    {t.mentions_vault && (
                      <Badge className="bg-[#F59E0B] text-black">vault-mention</Badge>
                    )}
                    {!t.enabled && <Badge variant="secondary">disabled</Badge>}
                    <span className="text-[10px] text-muted-foreground">
                      weight {t.weight} · v{t.version}
                    </span>
                  </div>
                  <p className="text-sm font-mono text-foreground/90 break-words">
                    {t.content}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => toggle(t)}
                    title={t.enabled ? "Disable" : "Enable"}
                    data-testid={`template-toggle-${t.id}`}
                  >
                    <Power size={14} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setEditing(t)}
                    data-testid={`template-edit-${t.id}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => remove(t.id)}
                    data-testid={`template-delete-${t.id}`}
                  >
                    <Trash2 size={14} className="text-[#FF4D4D]" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          </div>
        );
      })()}

      {editing && (
        <TemplateEditor
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Template editor dialog
// ---------------------------------------------------------------------
interface TemplateEditorProps {
  template: Template | null;
  onClose: () => void;
  onSaved: () => void;
}

function TemplateEditor({ template, onClose, onSaved }: TemplateEditorProps) {
  const [busy, setBusy] = useState<boolean>(false);
  const [triggerKey, setTriggerKey] = useState<string>(
    template?.trigger_key || "mint",
  );
  const [language, setLanguage] = useState<"en" | "fr">(
    template?.language || "en",
  );
  const [content, setContent] = useState<string>(template?.content || "");
  const [weight, setWeight] = useState<number>(template?.weight || 1.0);
  const [mentionsVault, setMentionsVault] = useState<boolean>(
    template?.mentions_vault || false,
  );
  const [enabled, setEnabled] = useState<boolean>(template?.enabled ?? true);

  const submit = async () => {
    if (content.trim().length === 0) {
      toast.error("Content cannot be empty");
      return;
    }
    setBusy(true);
    try {
      if (template) {
        await axios.patch(
          `${API}/api/admin/propaganda/templates/${template.id}`,
          { content, language, weight, mentions_vault: mentionsVault, enabled },
          { headers: authHeaders() },
        );
        toast.success("Template updated");
      } else {
        await axios.post(
          `${API}/api/admin/propaganda/templates`,
          {
            trigger_key: triggerKey,
            language,
            content,
            weight,
            mentions_vault: mentionsVault,
            enabled,
          },
          { headers: authHeaders() },
        );
        toast.success("Template created");
      }
      onSaved();
    } catch (err) {
      handleError(err, "Failed to save template");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle>
            {template ? "Edit template" : "New template"}
          </DialogTitle>
          <DialogDescription>
            Use placeholders like <code className="px-1 bg-muted rounded">{"{buy_link}"}</code>,{" "}
            <code className="px-1 bg-muted rounded">{"{mc_label}"}</code>,{" "}
            <code className="px-1 bg-muted rounded">{"{whale_amount}"}</code>{" "}
            — they're auto-filled at fire time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!template && (
            <div>
              <Label className="text-xs uppercase tracking-widest">Trigger</Label>
              <Select value={triggerKey} onValueChange={setTriggerKey}>
                <SelectTrigger className="font-mono" data-testid="template-trigger-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mint">mint</SelectItem>
                  <SelectItem value="mc_milestone">mc_milestone</SelectItem>
                  <SelectItem value="jeet_dip">jeet_dip</SelectItem>
                  <SelectItem value="whale_buy">whale_buy</SelectItem>
                  <SelectItem value="pumpswap_migration">pumpswap_migration</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest">Language</Label>
            <Select
              value={language}
              onValueChange={(v: string) => setLanguage(v as "en" | "fr")}
            >
              <SelectTrigger className="font-mono" data-testid="template-language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Content</Label>
            <Textarea
              value={content}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setContent(e.target.value)}
              rows={5}
              className="font-mono text-sm"
              maxLength={1000}
              data-testid="template-content-input"
            />
            <div className="text-[10px] text-muted-foreground text-right mt-1">
              {content.length}/1000
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Weight</Label>
              <Input
                type="number"
                value={weight}
                step={0.1}
                min={0.1}
                max={5}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setWeight(Number(e.target.value))}
                className="font-mono"
                data-testid="template-weight-input"
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <Switch
                  checked={mentionsVault}
                  onCheckedChange={setMentionsVault}
                  data-testid="template-mentions-vault"
                />
                <span>vault-mention</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  data-testid="template-enabled"
                />
                <span>enabled</span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="rounded-[var(--btn-radius)]"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="rounded-[var(--btn-radius)]"
            data-testid="template-save"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Queue tab
// ---------------------------------------------------------------------
function QueueTab() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  // Sprint 17.5d — "Verify X identity" diagnostic. Calls
  // /api/admin/propaganda/x-identity which in turn calls
  // GET /2/users/me with the Cabinet Vault credentials, so the
  // operator can confirm the OAuth account matches @Deepotus_AI
  // (or whatever they expect).
  const [identity, setIdentity] = useState<{
    ok: boolean;
    username?: string;
    name?: string;
    id?: string;
    error?: string;
    snippet?: string;
  } | null>(null);
  const [identityBusy, setIdentityBusy] = useState<boolean>(false);

  const checkIdentity = useCallback(async (): Promise<void> => {
    setIdentityBusy(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/propaganda/x-identity`,
        { headers: authHeaders() },
      );
      setIdentity(data);
      if (data?.ok) {
        toast.success(`Auth → @${data.username}`);
      } else {
        toast.error(`X identity check: ${data?.error || "unknown"}`);
      }
    } catch (err) {
      handleError(err, "Identity check failed");
    } finally {
      setIdentityBusy(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<{ items: QueueItem[] }>(
        `${API}/api/admin/propaganda/queue`,
        {
          headers: authHeaders(),
          params: statusFilter !== "all" ? { statuses: statusFilter } : {},
        },
      );
      // Alias `data.items` to satisfy exhaustive-deps (false positive).
      const nextQueue = data?.items ?? [];
      setItems(nextQueue);
    } catch (err) {
      handleError(err, "Failed to load queue");
    } finally {
      setLoading(false);
    }
    // exhaustive-deps false-positive (TS type confusion).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    void load();
    const id = setInterval(load, 6000); // gentle polling
    return () => clearInterval(id);
  }, [load]);

  const approve = async (id: string) => {
    try {
      await axios.post(
        `${API}/api/admin/propaganda/queue/${id}/approve`,
        {},
        { headers: authHeaders() },
      );
      toast.success("Approved — message scheduled.");
      await load();
    } catch (err) {
      handleError(err, "Failed to approve");
    }
  };

  const reject = async (id: string) => {
    const reason = window.prompt("Reject reason (optional):") || "";
    try {
      await axios.post(
        `${API}/api/admin/propaganda/queue/${id}/reject`,
        { reason },
        { headers: authHeaders() },
      );
      toast.success("Rejected.");
      await load();
    } catch (err) {
      handleError(err, "Failed to reject");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[180px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="proposed">Proposed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="killed">Killed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        {/* Sprint 17.5d — Verify X identity. Tells the operator
            which X handle the vault credentials authenticate as. */}
        <Button
          variant="outline"
          size="sm"
          onClick={(): void => {
            void checkIdentity();
          }}
          disabled={identityBusy}
          className="h-9 text-xs font-mono"
          data-testid="queue-verify-x-identity"
        >
          {identityBusy ? (
            <Loader2 size={14} className="mr-1.5 animate-spin" />
          ) : (
            <CheckCircle2 size={14} className="mr-1.5" />
          )}
          Verify X identity
        </Button>

        {identity && (
          <div
            className="text-xs font-mono px-2 py-1 rounded border"
            data-testid="queue-x-identity-result"
          >
            {identity.ok ? (
              <span className="text-[#18C964]">
                Auth →{" "}
                <a
                  href={`https://x.com/${identity.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-dotted"
                >
                  @{identity.username}
                </a>{" "}
                ({identity.name})
              </span>
            ) : (
              <span className="text-[#FF4D4D]">
                X identity: {identity.error}
              </span>
            )}
          </div>
        )}
      </div>

      {(() => {
        if (loading) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin" size={20} />
            </div>
          );
        }
        if (items.length === 0) {
          return (
            <div className="text-center text-sm text-muted-foreground py-12">
              Queue is empty.
            </div>
          );
        }
        return (
          <div className="space-y-2">
            {items.map((q) => (
              <QueueRow
                key={q.id}
                item={q}
                onApprove={() => approve(q.id)}
                onReject={() => reject(q.id)}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

interface QueueRowProps {
  item: QueueItem;
  onApprove: () => void;
  onReject: () => void;
}

function QueueRow({ item, onApprove, onReject }: QueueRowProps) {
  const statusColor: Record<string, string> = {
    proposed: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30",
    approved: "bg-[#0EA5E9]/15 text-[#0EA5E9] border-[#0EA5E9]/30",
    sent: "bg-[#18C964]/15 text-[#18C964] border-[#18C964]/30",
    rejected: "bg-muted text-muted-foreground border-border",
    killed: "bg-[#FF4D4D]/15 text-[#FF4D4D] border-[#FF4D4D]/30",
    failed: "bg-[#FF4D4D]/15 text-[#FF4D4D] border-[#FF4D4D]/30",
  };
  return (
    <div
      className="rounded-md border border-border bg-background/40 p-3"
      data-testid={`queue-row-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge variant="outline" className="font-mono">
              {item.trigger_key}
            </Badge>
            <span
              className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest border ${
                statusColor[item.status] || ""
              }`}
            >
              {item.status}
            </span>
            {item.manual && <Badge variant="secondary">manual</Badge>}
            <span className="text-[10px] text-muted-foreground">
              <Clock size={10} className="inline mr-1" />
              {new Date(item.proposed_at).toLocaleString()}
            </span>
          </div>
          <p className="text-sm font-mono text-foreground/90 break-words">
            {item.rendered_content}
          </p>
          <div className="text-[10px] text-muted-foreground mt-1 font-mono">
            platforms: {item.platforms.join(", ") || "—"} ·
            scheduled:{" "}
            {item.scheduled_for
              ? new Date(item.scheduled_for).toLocaleString()
              : "—"}
          </div>

          {/* Sprint 17.5d — direct links to the published artefacts.
              Once dispatch_worker stores ``results.<platform>.platform_message_id``
              (tweet ID for X, message ID for Telegram), we render
              clickable "View on X / Telegram" badges so the operator
              can verify the post landed on the right account. If the
              link 404s on X, the OAuth credentials in the vault are
              for a DIFFERENT X account than the one the operator
              expects (use the "Verify X identity" button below the
              tab header to check). */}
          {item.results && Object.keys(item.results).length > 0 && (
            <div
              className="mt-1 flex flex-wrap items-center gap-1.5"
              data-testid={`queue-item-result-links-${item.id}`}
            >
              {Object.entries(item.results).map(([plat, r]) => {
                const tweetId = r?.platform_message_id;
                if (!tweetId || tweetId === "dry-run" || r?.outcome !== "sent") {
                  return null;
                }
                let href: string | null = null;
                if (plat === "x") {
                  // X resolves /i/web/status/<id> to the canonical tweet
                  // URL regardless of which @username posted it — so the
                  // 404 vs redirect tells us instantly whether the
                  // OAuth account matches expectations.
                  href = `https://x.com/i/web/status/${tweetId}`;
                } else if (plat === "telegram") {
                  // Telegram message links require the channel/chat
                  // username; without it we just surface the message ID.
                  href = null;
                }
                return href ? (
                  <a
                    key={plat}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#18C964]/40 bg-[#18C964]/10 text-[10px] font-mono text-[#18C964] hover:bg-[#18C964]/20 transition-colors"
                    data-testid={`queue-item-view-${plat}-${item.id}`}
                  >
                    View on {plat === "x" ? "X" : plat} →
                  </a>
                ) : (
                  <span
                    key={plat}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border text-[10px] font-mono text-muted-foreground"
                  >
                    {plat}: {tweetId}
                  </span>
                );
              })}
            </div>
          )}

          {item.error && (
            <div className="mt-1 text-[11px] text-[#FF4D4D] font-mono">
              error: {item.error}
              {/* Sprint 17.5d/e — actionable hint for the X billing / tier
                  refusal codes + parsed 400 reasons. The error string is
                  per-platform and may contain prefixes like
                  "x=x_billing_required". We match the substring to stay
                  tolerant of future multi-platform error formats. */}
              {(item.error.includes("x_billing_required") ||
                item.error.includes("x_tier_locked") ||
                item.error.includes("http_402") ||
                item.error.includes("http_403")) && (
                <div className="mt-1 text-foreground/85 font-sans whitespace-normal">
                  ⚠ X HTTP 402 / 403 — votre compte X n'est pas sur un
                  tier autorisant <span className="font-mono">POST /2/tweets</span>.
                  Vérifiez sur{" "}
                  <a
                    href="https://developer.x.com/en/portal/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted"
                    data-testid="queue-error-x-portal-link"
                  >
                    developer.x.com
                  </a>{" "}
                  que l'App est en Basic ($200/mo) ou en Pay-Per-Use
                  avec crédits actifs pour cet endpoint. Le code envoie
                  bien le tweet — c'est X qui refuse au billing.
                </div>
              )}
              {item.error.includes("x_duplicate_content") && (
                <div className="mt-1 text-foreground/85 font-sans whitespace-normal">
                  ⚠ X refuse le tweet : <strong>contenu dupliqué</strong>.
                  X bloque les tweets identiques (texte exact) postés
                  récemment depuis le même compte. Régénérez la preview
                  ou attendez ~24h. Astuce : rajoutez un suffixe unique
                  (timestamp, emoji différent) si vous voulez vraiment
                  re-poster le même message.
                </div>
              )}
              {item.error.includes("x_text_too_long") && (
                <div className="mt-1 text-foreground/85 font-sans whitespace-normal">
                  ⚠ Le tweet dépasse 280 caractères. Le dispatcher tronque
                  normalement à 260, vérifiez la preview pour un edge-case
                  (caractères Unicode comptant double).
                </div>
              )}
              {item.error.includes("x_policy_violation") && (
                <div className="mt-1 text-foreground/85 font-sans whitespace-normal">
                  ⚠ X juge le tweet contraire à ses règles (spam,
                  contenu sensible, mots-clés filtrés...). Reformulez
                  la prompt LLM pour adoucir le ton.
                </div>
              )}
              {item.error.includes("x_invalid_payload") && (
                <div className="mt-1 text-foreground/85 font-sans whitespace-normal">
                  ⚠ Payload invalide côté X (caractères mal encodés,
                  champ manquant). Voir le snippet ci-dessous.
                </div>
              )}
              {/* Sprint 17.5d/e — surface the per-platform response_snippet
                  so the operator sees X's exact verdict (parsed
                  detail/message). Helps when the error code doesn't map
                  cleanly to a known reason. */}
              {item.results &&
                Object.entries(item.results)
                  .filter(([, r]) => r?.outcome !== "sent" && (r?.response_snippet || r?.error))
                  .slice(0, 3)
                  .map(([plat, r]) => (
                    <div
                      key={plat}
                      className="mt-1 text-foreground/70 font-mono text-[10px] whitespace-pre-wrap break-words"
                      data-testid={`queue-item-snippet-${plat}-${item.id}`}
                    >
                      ↳ {plat}: {r.response_snippet || r.error}
                    </div>
                  ))}
            </div>
          )}
          {item.reject_reason && (
            <div className="mt-1 text-[11px] text-muted-foreground font-mono">
              rejected: {item.reject_reason}
            </div>
          )}
        </div>
        {item.status === "proposed" && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              onClick={onApprove}
              className="rounded-[var(--btn-radius)]"
              data-testid={`queue-approve-${item.id}`}
            >
              <CheckCircle2 size={14} className="mr-1" /> Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onReject}
              className="rounded-[var(--btn-radius)]"
              data-testid={`queue-reject-${item.id}`}
            >
              <X size={14} className="mr-1" /> Reject
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Activity tab
// ---------------------------------------------------------------------
function ActivityTab() {
  const [items, setItems] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<{ items: ActivityEvent[] }>(
        `${API}/api/admin/propaganda/activity`,
        { headers: authHeaders(), params: { limit: 100 } },
      );
      // Alias `data.items` to satisfy exhaustive-deps (false positive).
      const nextEvents = data?.items ?? [];
      setItems(nextEvents);
    } catch (err) {
      handleError(err, "Failed to load activity");
    } finally {
      setLoading(false);
    }
    // exhaustive-deps false-positive (TS type confusion).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const colorByType: Record<string, string> = {
    fire: "text-[#0EA5E9]",
    fire_skip: "text-muted-foreground",
    fire_skip_disabled: "text-muted-foreground",
    fire_skip_panic: "text-[#FF4D4D]",
    fire_no_template: "text-[#F59E0B]",
    approve: "text-[#18C964]",
    reject: "text-[#F59E0B]",
    panic_on: "text-[#FF4D4D]",
    panic_off: "text-[#18C964]",
  };

  return (
    <div>
      {(() => {
        if (loading) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin" size={20} />
            </div>
          );
        }
        if (items.length === 0) {
          return (
            <div className="text-center text-sm text-muted-foreground py-12">
              No activity recorded yet.
            </div>
          );
        }
        return (
          <ScrollArea className="h-[480px] pr-2">
            <ul className="space-y-1.5 font-mono text-[12px]">
              {items.map((e) => (
                <li
                  key={e.id}
                  className="flex items-start gap-3 rounded border border-border/60 bg-background/30 px-3 py-1.5"
                  data-testid={`activity-row-${e.id}`}
                >
                  <Activity size={11} className="mt-1 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`uppercase tracking-widest ${
                          colorByType[e.type] || "text-foreground"
                        }`}
                      >
                        {e.type.replace(/_/g, " ")}
                      </span>
                      {e.trigger_key && (
                        <Badge variant="outline">{e.trigger_key}</Badge>
                      )}
                      {Boolean(e.meta?.policy) && (
                        <Badge variant="outline">
                          policy: {String(e.meta.policy)}
                        </Badge>
                      )}
                      {e.queue_item_id && (
                        <span className="text-muted-foreground truncate text-[10px]">
                          item: {e.queue_item_id.slice(0, 8)}…
                        </span>
                      )}
                    </div>
                    {e.meta && Object.keys(e.meta).length > 0 && (
                      <div className="text-muted-foreground text-[10px] mt-0.5 truncate">
                        {Object.entries(e.meta)
                          .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                  <span className="text-muted-foreground text-[10px] shrink-0">
                    {new Date(e.at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          </ScrollArea>
        );
      })()}

      {!loading && items.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
          <AlertTriangle size={11} />
          Audit feed never persists secret values — only trigger keys, item ids and metadata.
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------
// Tone & LLM tab (Sprint 13.2) — controls the LLM hybrid mix and the
// "weary intelligence officer" personality prompt that drives rewrites.
// ---------------------------------------------------------------------
interface ToneTabProps {
  settings: Settings;
  onSaved: () => void | Promise<void>;
}

function ToneTab({ settings, onSaved }: ToneTabProps) {
  const [enabled, setEnabled] = useState<boolean>(settings.llm_enabled);
  const [ratio, setRatio] = useState<number>(settings.llm_enhance_ratio);
  const [prompt, setPrompt] = useState<string>(settings.personality_prompt);
  const [provider, setProvider] = useState<string>(settings.llm_provider);
  const [model, setModel] = useState<string>(settings.llm_model);
  const [busy, setBusy] = useState<boolean>(false);

  const dirty = useMemo(
    () =>
      enabled !== settings.llm_enabled ||
      Math.abs(ratio - settings.llm_enhance_ratio) > 0.001 ||
      prompt.trim() !== settings.personality_prompt.trim() ||
      provider !== settings.llm_provider ||
      model !== settings.llm_model,
    [enabled, ratio, prompt, provider, model, settings],
  );

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/api/admin/propaganda/settings`,
        {
          llm_enabled: enabled,
          llm_enhance_ratio: ratio,
          personality_prompt: prompt,
          llm_provider: provider,
          llm_model: model,
        },
        { headers: authHeaders() },
      );
      toast.success("Tone settings saved.");
      await onSaved();
    } catch (err) {
      handleError(err, "Failed to save tone settings");
    } finally {
      setBusy(false);
    }
  };

  const ratioLabel = `${Math.round(ratio * 100)}%`;
  const ratioColor = (() => {
    if (ratio >= 0.7) return "text-[#FF4D4D]";
    if (ratio >= 0.4) return "text-[#F59E0B]";
    return "text-[#18C964]";
  })();

  return (
    <div className="space-y-6" data-testid="propaganda-tone-tab">
      {/* Hybrid mix card */}
      <section className="rounded-md border border-border bg-background/40 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-[#0EA5E9]" />
          <h3 className="text-sm font-medium">LLM hybrid mix</h3>
        </div>
        <p className="text-[12px] text-muted-foreground -mt-2">
          When the engine fires, it picks a templated message at random. With
          probability <strong className={ratioColor}>{ratioLabel}</strong> we
          ask the LLM to rewrite that message in the persona below — keeping
          the meaning, the placeholders and the platform char budget intact.
          Lower the ratio for cheaper, more predictable output; raise it for
          more variety.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <label
            className="flex items-center gap-2 cursor-pointer text-xs"
            data-testid="tone-llm-enabled-label"
          >
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="tone-llm-enabled"
            />
            <span className="font-mono uppercase tracking-widest text-[10px]">
              {enabled ? "LLM ENABLED" : "TEMPLATE-ONLY"}
            </span>
          </label>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest">
            Enhance ratio — {ratioLabel}
          </Label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={ratio}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRatio(Number(e.target.value))}
            disabled={!enabled}
            className="w-full mt-2 accent-[#F59E0B] disabled:opacity-50"
            data-testid="tone-ratio-slider"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground mt-1">
            <span>0% (templates only)</span>
            <span>50% hybrid</span>
            <span>100% (always LLM rewrite)</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger
                className="font-mono"
                data-testid="tone-provider-select"
                disabled={!enabled}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">openai</SelectItem>
                <SelectItem value="anthropic">anthropic</SelectItem>
                <SelectItem value="gemini">gemini</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Model</Label>
            <Input
              value={model}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setModel(e.target.value)}
              placeholder="gpt-4o-mini"
              disabled={!enabled}
              className="font-mono"
              data-testid="tone-model-input"
            />
          </div>
        </div>
      </section>

      {/* Personality prompt */}
      <section className="rounded-md border border-border bg-background/40 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <SettingsIcon size={16} className="text-[#F59E0B]" />
          <h3 className="text-sm font-medium">Personality prompt</h3>
        </div>
        <p className="text-[12px] text-muted-foreground">
          Drives every LLM rewrite. Tweak the voice (e.g. add new vocabulary
          like <code>Bunker</code>, <code>Clearance Level</code>) without
          touching code. Keep the integrity rules (placeholders, char budget,
          no hashtags) — they're enforced by the post-processor anyway, but
          telling the LLM up-front saves tokens.
        </p>
        <Textarea
          value={prompt}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setPrompt(e.target.value)}
          rows={12}
          maxLength={4000}
          disabled={!enabled}
          className="font-mono text-xs leading-relaxed disabled:opacity-50"
          data-testid="tone-personality-prompt"
        />
        <div className="text-[10px] text-muted-foreground text-right font-mono">
          {prompt.length}/4000
        </div>
      </section>

      {/* Save bar */}
      <div className="sticky bottom-2 flex items-center justify-end gap-2 z-10">
        <span className="text-[11px] text-muted-foreground font-mono">
          {dirty ? "unsaved changes" : "synced"}
        </span>
        <Button
          onClick={save}
          disabled={!dirty || busy}
          className="rounded-[var(--btn-radius)]"
          data-testid="tone-save"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Save tone settings"}
        </Button>
      </div>
    </div>
  );
}
